<template>
  <a
    v-if="actionItem && isUrl && subActions.length == 0"
    :href="actionItem.url"
    :class="actionItem.cssClass"
  >
    {{ actionItem.title }}
  </a>
  <a
    v-else-if="actionItem && isAction && subActions.length == 0"
    href="#"
    :class="actionItem.cssClass"
    @click.prevent="emitAction(actionItem)"
  >
    {{ actionItem.title }}
  </a>
  <div
    v-else-if="actionItem && subActions.length > 0"
    :class="dropdownClass"
  >
    <a
      v-if="isDropdown"
      :class="actionItem.cssClass"
      href="#"
      role="button"
      data-bs-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {{ actionItem.title }}
    </a>
    <a
      v-if="isUrl"
      :href="actionItem.url"
      :class="actionItem.cssClass"
    >
      {{ actionItem.title }}
    </a>
    <a
      v-else-if="isAction"
      href="#"
      :class="actionItem.cssClass"
      @click.prevent="emitAction(actionItem)"
    >
      {{ actionItem.title }}
    </a>
    <button
      v-if="!isDropdown"
      type="button"
      :class="[mainActionCssClass, 'dropdown-toggle dropdown-toggle-split']"
      data-bs-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      data-bs-boundary="viewport"
      data-bs-popper-config='{"strategy":"fixed"}'
    >
      <span class="visually-hidden">Toggle Dropdown</span>
    </button>
    <div :class="['dropdown-menu', { 'dropdown-menu-end': dropdownAlignRight }]">
      <template
        v-for="(subAction, idx) of subActions"
        :key="idx"
      >
        <a
          v-if="subAction.type == 1"
          :href="subAction.url"
          class="dropdown-item"
        >
          {{ subAction.title }}
        </a>
        <a
          v-else-if="subAction.type == 2"
          href="#"
          class="dropdown-item"
          @click.prevent="emitAction(subAction)"
        >
          {{ subAction.title }}
        </a>
        <div
          v-else-if="subAction.type == 3"
          class="dropdown-divider"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import {isNonEmptyString} from '@/js/js-utils'
import type {GridAction, GridItemAction} from './types'
import {GridActionType} from './types'

const emit = defineEmits<{
  (e: 'item-action', item: Record<string, any> | null, action: GridAction): void
}>()

const props = defineProps({
  item: {default: null, type: Object as () => Record<string, any> | null},
  action: {default: null, type: Object as () => GridItemAction | null},
  dropdownAlignRight: {default: true, type: Boolean},
})

const actionItem = computed((): GridAction | null => {
  return itemAction2Action(props.action)
})

const isUrl = computed((): boolean => {
  return actionItem.value?.type == GridActionType.url
})

const isAction = computed((): boolean => {
  return actionItem.value?.type == GridActionType.action
})

const isDropdown = computed((): boolean => {
  return actionItem.value?.type == GridActionType.dropdown
})

const dropdownClass = computed((): string => {
  if (actionItem.value?.type == GridActionType.dropdown) {
    return 'dropdown'
  }
  return 'btn-group'
})

const subActions = computed((): GridAction[] => {
  const result: GridAction[] = []
  const subActions = props.action?.subActions as GridItemAction[]
  if (Array.isArray(subActions)) {
    let skipSeparator = true
    for (const itemAction of subActions) {
      if (itemAction.type == 'separator') {
        if (!skipSeparator) {
          result.push({
            type: GridActionType.separator,
            cssClass: '',
            url: '',
            title: '',
            name: '',
          })
          // don't want two separators in a row
          skipSeparator = true
        }
      } else {
        const action = itemAction2Action(itemAction)
        if (action) {
          skipSeparator = false
          result.push(action)
        }
      }
    }
  }
  return result
})

const mainActionCssClass = computed((): string | null => {
  if (isUrl.value || isAction.value) {
    return actionItem.value?.cssClass || null
  }
  return null
})

const emitAction = (action: GridAction | null): void => {
  if (action) {
    emit('item-action', props.item, action)
  }
}

const itemAction2Action = (itemAction: GridItemAction | null): GridAction | null => {
  if (itemAction == null) {
    return null
  }

  if (itemAction.type == 'url' || itemAction.type == 'action') {
    let url = null
    if (isNonEmptyString(itemAction.url)) {
      url = itemAction.url
    } else if (isNonEmptyString(itemAction.link)) {
      if (props.item?.links) {
        url = props.item.links[itemAction.link]
      }
    }

    if (isNonEmptyString(url)) {
      return {
        type: itemAction.type == 'url' ? GridActionType.url : GridActionType.action,
        title: itemAction.title,
        url: url,
        options: itemAction.options,
        cssClass: isNonEmptyString(itemAction.cssClass) ? itemAction.cssClass : 'btn btn-sm btn-success',
        name: isNonEmptyString(itemAction.name) ? itemAction.name : ''
      }
    }
  } else if (itemAction.type == 'dropdown') {
    return {
      type: GridActionType.dropdown,
      title: itemAction.title,
      url: '',
      options: itemAction.options,
      cssClass: isNonEmptyString(itemAction.cssClass) ? itemAction.cssClass : 'btn btn-sm btn-success dropdown-toggle',
      name: isNonEmptyString(itemAction.name) ? itemAction.name : ''
    }
  }

  return null
}
</script>
